import SvgIcon from '@/components/Icon/SvgIcon';
import { Card } from '@/components/ui/card';
import { Counter } from '@/components/ui/shadcn-io/counter';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  ContainerEntry,
  ItemTypeEntry,
  useOrderFormContext,
} from '../PublicEventOrderForm/OrderFormContext';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { Separator } from '@/components/ui/separator';
import {
  ContainerTypeSegment,
  MerchantOrderItemUnit,
} from '@/graphql/generatedTypes';
import { IconName } from '@/utils/iconName';
import { useFormState } from 'react-hook-form';
import i18n from '@/utils/i18n';
import { centsToPrice } from '@/utils/formatter';
import { OrderFormContainerTypeThumbnail } from '../PublicEventOrderForm/components/OrderFormContainerTypeThumbnail';

type ItemLocationOrderSectionProps = {
  itemLocationId: string;
  containers: ContainerEntry[];
  /** Container stations the event allows, listed with the containers. */
  itemTypes?: ItemTypeEntry[];
  disabled: boolean;
  onChangeQuantity: (containerTypeId: number, quantity: number) => void;
  onChangeItemTypeQuantity?: (itemTypeId: number, quantity: number) => void;
  currency: string | null;
};

export function ItemLocationOrderSection({
  containers,
  itemTypes = [],
  disabled,
  onChangeQuantity,
  onChangeItemTypeQuantity,
  currency,
}: ItemLocationOrderSectionProps) {
  return (
    <Tabs defaultValue="all">
      <TabsList className="tw:mb-2">
        <TabTriggerWithBadge
          containers={containers}
          extraCount={itemTypes.length}
        />
        <TabTriggerWithBadge
          containers={containers}
          segment={ContainerTypeSegment.Food}
        />
        <TabTriggerWithBadge
          containers={containers}
          segment={ContainerTypeSegment.Drinks}
        />
        <TabTriggerWithBadge
          containers={containers}
          segment={ContainerTypeSegment.Cutlery}
        />
      </TabsList>
      <TabsContent value="all">
        <ContainerCountsForLocation
          onChangeQuantity={onChangeQuantity}
          containers={containers}
          itemTypes={itemTypes}
          onChangeItemTypeQuantity={onChangeItemTypeQuantity}
          disabled={disabled}
          currency={currency}
        />
      </TabsContent>
      <TabsContent value={ContainerTypeSegment.Food}>
        <ContainerCountsForLocation
          onChangeQuantity={onChangeQuantity}
          containers={containers}
          segment={ContainerTypeSegment.Food}
          disabled={disabled}
          currency={currency}
        />
      </TabsContent>
      <TabsContent value={ContainerTypeSegment.Drinks}>
        <ContainerCountsForLocation
          onChangeQuantity={onChangeQuantity}
          containers={containers}
          segment={ContainerTypeSegment.Drinks}
          disabled={disabled}
          currency={currency}
        />
      </TabsContent>
      <TabsContent value={ContainerTypeSegment.Cutlery}>
        <ContainerCountsForLocation
          onChangeQuantity={onChangeQuantity}
          containers={containers}
          segment={ContainerTypeSegment.Cutlery}
          disabled={disabled}
          currency={currency}
        />
      </TabsContent>
    </Tabs>
  );
}

ItemLocationOrderSection.Connected =
  function ItemLocationOrderSectionConnected({
    itemLocationId,
  }: {
    itemLocationId: string;
  }) {
    const { formControl, order, updateOrder, updateItemTypeOrder, currency } =
      useOrderFormContext();
    const { disabled } = useFormState({
      control: formControl,
    });

    const containers = Object.values(order[itemLocationId]?.containers ?? {});
    const itemTypes = Object.values(order[itemLocationId]?.itemTypes ?? {});

    const handleChangeQuantity = (
      containerTypeId: number,
      quantity: number,
    ) => {
      updateOrder({
        itemLocationId,
        containerTypeId,
        quantity,
      });
    };

    return (
      <ItemLocationOrderSection
        itemLocationId={itemLocationId}
        containers={containers}
        itemTypes={itemTypes}
        disabled={disabled}
        onChangeQuantity={handleChangeQuantity}
        onChangeItemTypeQuantity={(itemTypeId, quantity) =>
          updateItemTypeOrder({ itemLocationId, itemTypeId, quantity })
        }
        currency={currency}
      />
    );
  };

ItemLocationOrderSection.Admin = function ItemLocationOrderSectionAdmin({
  itemLocationId,
  containers,
  itemTypes,
  disabled,
  onChangeQuantity,
  onChangeItemTypeQuantity,
  currency,
}: {
  itemLocationId: string;
  containers: ContainerEntry[];
  itemTypes?: ItemTypeEntry[];
  disabled: boolean;
  onChangeQuantity: (containerTypeId: number, quantity: number) => void;
  onChangeItemTypeQuantity?: (itemTypeId: number, quantity: number) => void;
  currency: string;
}) {
  return (
    <ItemLocationOrderSection
      itemLocationId={itemLocationId}
      containers={containers}
      itemTypes={itemTypes}
      disabled={disabled}
      onChangeQuantity={onChangeQuantity}
      onChangeItemTypeQuantity={onChangeItemTypeQuantity}
      currency={currency}
    />
  );
};

function TabTriggerWithBadge({
  containers,
  segment,
  extraCount = 0,
}: {
  containers: ContainerEntry[];
  segment?: ContainerTypeSegment;
  /** Rows listed on this tab that are not containers (container stations). */
  extraCount?: number;
}) {
  let count = extraCount;

  containers.forEach((c) => {
    if (!segment || c.info.segment === segment) {
      count += 1;
    }
  });

  if (segment && count === 0) {
    return null;
  }

  const label = {
    all: i18n.t('EventOrder:all'),
    [ContainerTypeSegment.Cutlery]: i18n.t('EventOrder:cutlery'),
    [ContainerTypeSegment.Drinks]: i18n.t('EventOrder:drinks'),
    [ContainerTypeSegment.Food]: i18n.t('EventOrder:food'),
  }[segment ?? 'all'];

  const icon = {
    all: 'container-bowl-cup-stock',
    [ContainerTypeSegment.Cutlery]: 'knife-spoon-restaurant',
    [ContainerTypeSegment.Drinks]: 'container-bowl-cup-stock',
    [ContainerTypeSegment.Food]: 'container-bowl',
  }[segment ?? 'all'] as IconName;

  return (
    <TabsTrigger
      value={segment ?? 'all'}
      className="tw:flex tw:items-center tw:gap-1.5 tw:md:gap-2 tw:px-2 tw:py-1 tw:opacity-100! tw:cursor-pointer"
    >
      <SvgIcon
        className={cn('tw:hidden', 'tw:md:block')}
        name={icon}
        size={16}
      />
      {label}
      {count > 0 && <CountBadge number={count} />}
    </TabsTrigger>
  );
}

/**
 * One row per orderable type: containers (in boxes) and, on the "all" tab,
 * container stations (in pieces). Partners see one list; the unit tells them
 * apart.
 */
function ContainerCountsForLocation({
  containers,
  itemTypes = [],
  segment,
  disabled,
  onChangeQuantity,
  onChangeItemTypeQuantity,
  currency,
}: {
  containers: ContainerEntry[];
  itemTypes?: ItemTypeEntry[];
  segment?: ContainerTypeSegment;
  disabled: boolean;
  onChangeQuantity: (containerTypeId: number, quantity: number) => void;
  onChangeItemTypeQuantity?: (itemTypeId: number, quantity: number) => void;
  currency: string | null;
}) {
  const isMobile = useIsMobile();

  const filteredContainers: ContainerEntry[] = [];
  containers.forEach((c) => {
    if (segment && c.info.segment.toLowerCase() !== segment.toLowerCase()) {
      return;
    }
    filteredContainers.push(c);
  });
  const itemTypeRows = onChangeItemTypeQuantity ? itemTypes : [];

  const rows: React.ReactNode[] = [
    ...filteredContainers.map((container) =>
      isMobile ? (
        <OrderItem
          key={`container-${container.info.id}`}
          container={container}
          onChangeQuantity={onChangeQuantity}
          disabled={disabled}
          vertical
          currency={currency}
        />
      ) : (
        <OrderItem
          key={`container-${container.info.id}`}
          container={container}
          disabled={disabled}
          onChangeQuantity={onChangeQuantity}
          currency={currency}
        />
      ),
    ),
    ...itemTypeRows.map((line) => (
      <ItemTypeOrderItem
        key={`item-type-${line.info.id}`}
        line={line}
        currency={currency}
        disabled={disabled}
        onChangeQuantity={onChangeItemTypeQuantity!}
        vertical={isMobile}
      />
    )),
  ];

  if (isMobile) {
    return (
      <div className="tw:flex tw:flex-col tw:gap-4 tw:py-4 tw:animate-fade-in tw:animation-duration-500">
        {rows.map((row, index) => (
          <div
            key={(row as React.ReactElement).key}
            className="tw:flex tw:flex-col tw:gap-4"
          >
            {row}
            {index !== rows.length - 1 && <Separator className="tw:my-4" />}
          </div>
        ))}
      </div>
    );
  }

  return (
    <Card className="tw:shadow-none tw:animate-fade-in tw:animation-duration-500">
      <Table className="tw:w-full">
        <TableHeader>
          <TableRow>
            <TableHead colSpan={2} className="tw:py-4">
              <div className="tw:flex tw:items-center tw:justify-end tw:px-20 tw:gap-2">
                <SvgIcon
                  name="box-filled"
                  size={16}
                  className="tw:text-eurobox-320"
                />
                {i18n.t('EventOrder:boxes')}
              </div>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={(row as React.ReactElement).key}>
              <TableCell className="tw:py-3 tw:px-6!">{row}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function OrderItem({
  container,
  disabled = false,
  onChangeQuantity,
  vertical = false,
  currency,
}: {
  container: ContainerEntry;
  disabled?: boolean;
  onChangeQuantity: (containerTypeId: number, quantity: number) => void;
  vertical?: boolean;
  currency: string | null;
}) {
  const { info, price, quantity, unit } = container;

  const handleChangeQuantity = (quantity: number) => {
    if (quantity < 0) {
      return;
    }
    onChangeQuantity?.(info.id, quantity);
  };

  const priceInfo = [
    `${i18n.t('EventOrder:VPE')}: ${info.packingUnit} ${i18n.t('EventOrder:piece')}`,
    `${i18n.t('EventOrder:rentPrice')}: ${centsToPrice(price.rentalPrice, currency)}`,
    `${i18n.t('EventOrder:cleaning')}: ${centsToPrice(price.cleaningPrice, currency)}`,
    `${i18n.t('EventOrder:depositPrice')}: ${centsToPrice(price.depositPrice, currency)}`,
  ].join(' / ');

  return (
    <div
      className={cn(
        'tw:flex tw:flex-col tw:items-start tw:gap-4',
        'tw:md:flex-row tw:md:items-center',
      )}
    >
      <div
        className={cn(
          'tw:flex tw:flex-row-reverse tw:items-start tw:justify-between tw:gap-4 tw:w-full',
          'tw:md:flex-row tw:md:items-center tw:md:justify-start tw:md:w-[unset]',
        )}
      >
        <Checkbox
          disabled={disabled}
          checked={quantity > 0}
          className="tw:size-5 tw:not-dark:data-[state=checked]:bg-success-main tw:not-dark:data-[state=checked]:border-success-main"
          onCheckedChange={() => handleChangeQuantity(quantity > 0 ? 0 : 1)}
        />
        <OrderFormContainerTypeThumbnail
          image={info.imageUrl}
          color={info.color}
        />
      </div>

      <div className="tw:flex tw:flex-1 tw:flex-col tw:pr-6">
        <span className="tw:font-medium tw:text-sm">{info.name}</span>
        <span className="tw:text-muted-foreground tw:text-xs">{priceInfo}</span>
      </div>

      <div className="tw:flex tw:items-center tw:justify-between tw:relative">
        <Counter
          number={quantity}
          suffix={vertical ? ' x' : undefined}
          setNumber={handleChangeQuantity}
          className={'tw:gap-5'}
          buttonProps={{ disabled, size: 'lg' }}
        >
          {vertical && (
            <SvgIcon
              name="box-filled"
              size={20}
              className="tw:text-eurobox-320 tw:-ml-1"
            />
          )}
        </Counter>
        {Number(quantity ?? 0) > 0 && (
          <span className="tw:font-medium tw:text-xs tw:text-neutral-400 tw:absolute tw:right-0 tw:left-0 tw:text-center tw:-bottom-6">
            ={' '}
            {unit === MerchantOrderItemUnit.Boxes
              ? quantity * info.packingUnit
              : quantity}{' '}
            {i18n.t('EventOrder:pcs')}
          </span>
        )}
      </div>
    </div>
  );
}

/** A container station row: same layout as a container row, counted in pieces. */
function ItemTypeOrderItem({
  line,
  currency,
  disabled = false,
  onChangeQuantity,
  vertical = false,
}: {
  line: ItemTypeEntry;
  currency: string | null;
  disabled?: boolean;
  onChangeQuantity: (itemTypeId: number, quantity: number) => void;
  vertical?: boolean;
}) {
  const { info, price, quantity } = line;

  const priceInfo = price
    ? [
        `${i18n.t('EventOrder:rentPrice')}: ${centsToPrice(price.rentalPrice, currency)}`,
        `${i18n.t('EventOrder:cleaning')}: ${centsToPrice(price.cleaningPrice, currency)}`,
        `${i18n.t('EventOrder:depositPrice')}: ${centsToPrice(price.depositPrice, currency)}`,
      ].join(' / ')
    : null;

  const handleChangeQuantity = (next: number) => {
    if (next < 0) {
      return;
    }
    onChangeQuantity(info.id, next);
  };

  return (
    <div
      className={cn(
        'tw:flex tw:flex-col tw:items-start tw:gap-4',
        'tw:md:flex-row tw:md:items-center',
      )}
    >
      <div
        className={cn(
          'tw:flex tw:flex-row-reverse tw:items-start tw:justify-between tw:gap-4 tw:w-full',
          'tw:md:flex-row tw:md:items-center tw:md:justify-start tw:md:w-[unset]',
        )}
      >
        <Checkbox
          disabled={disabled}
          checked={quantity > 0}
          className="tw:size-5 tw:not-dark:data-[state=checked]:bg-success-main tw:not-dark:data-[state=checked]:border-success-main"
          onCheckedChange={() => handleChangeQuantity(quantity > 0 ? 0 : 1)}
        />
        <OrderFormContainerTypeThumbnail
          image={info.imageUrl}
          color={info.color}
        />
      </div>

      <div className="tw:flex tw:flex-1 tw:flex-col tw:pr-6">
        <span className="tw:font-medium tw:text-sm">{info.name}</span>
        {priceInfo && (
          <span className="tw:text-muted-foreground tw:text-xs">
            {priceInfo}
          </span>
        )}
      </div>

      <div className="tw:flex tw:items-center tw:justify-between tw:relative">
        <Counter
          number={quantity}
          suffix={vertical ? ` ${i18n.t('EventOrder:pcs')}` : undefined}
          setNumber={handleChangeQuantity}
          className={'tw:gap-5'}
          buttonProps={{ disabled, size: 'lg' }}
        />
        {!vertical && (
          <span className="tw:font-medium tw:text-xs tw:text-neutral-400 tw:absolute tw:right-0 tw:left-0 tw:text-center tw:-bottom-6">
            {i18n.t('EventOrder:pcs')}
          </span>
        )}
      </div>
    </div>
  );
}

function CountBadge({ number }: { number: string | number }) {
  return (
    <div className="tw:flex tw:h-4 tw:min-w-4 tw:items-center tw:justify-center tw:rounded-full tw:bg-neutral-300 tw:text-center tw:text-xs tw:font-medium tw:text-neutral-900">
      {number}
    </div>
  );
}
